import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d658kfk5l058qvghfu90', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      <main className="pt-[90px]">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-[#0A0A0A] to-black py-32">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center px-4 py-2 border border-copper/30 rounded-full mb-8">
                <span className="text-xs uppercase tracking-wider text-copper">Contact Us</span>
              </div>
              <h1 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                Let's Transform{' '}
                <span className="bg-copper-gradient bg-clip-text text-transparent">
                  Your Operations
                </span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed">
                Schedule a confidential consultation with MACGOC's executive team to discuss your strategic challenges and explore how we can help achieve your objectives.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left: Contact Info */}
              <div className="space-y-12">
                <div>
                  <h2 className="text-4xl font-bold text-white mb-6">Get in Touch</h2>
                  <p className="text-lg text-gray-400 leading-relaxed">
                    Whether you're seeking strategic advisory, operational consulting, or specialized expertise across our five divisions, we're here to help. Complete the form and our team will respond within 24 hours.
                  </p>
                </div>

                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-copper/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-copper"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Email</h3>
                      <p className="text-base text-gray-400">info@macgoc.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-copper/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-copper"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Location</h3>
                      <p className="text-base text-gray-400">United States</p>
                    </div>
                  </div>
                </div>

                <div className="pt-8">
                  <h3 className="text-lg font-semibold text-white mb-4">Follow Us</h3>
                  <div className="flex items-center gap-4">
                    <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-charcoal border border-charcoal-light rounded-full flex items-center justify-center hover:border-copper transition-colors">
                      <i className="ri-linkedin-line text-xl text-copper"></i>
                    </a>
                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-charcoal border border-charcoal-light rounded-full flex items-center justify-center hover:border-copper transition-colors">
                      <i className="ri-twitter-x-line text-xl text-copper"></i>
                    </a>
                    <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-charcoal border border-charcoal-light rounded-full flex items-center justify-center hover:border-copper transition-colors">
                      <i className="ri-youtube-line text-xl text-copper"></i>
                    </a>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="bg-charcoal border border-charcoal-light rounded-3xl p-10">
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-charcoal-dark border border-charcoal-light rounded-xl h-12 px-4 text-base text-white placeholder-gray-600 focus:outline-none focus:border-copper transition-colors"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-charcoal-dark border border-charcoal-light rounded-xl h-12 px-4 text-base text-white placeholder-gray-600 focus:outline-none focus:border-copper transition-colors"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-white mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-charcoal-dark border border-charcoal-light rounded-xl h-12 px-4 text-base text-white placeholder-gray-600 focus:outline-none focus:border-copper transition-colors"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-white mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full bg-charcoal-dark border border-charcoal-light rounded-xl h-12 px-4 text-base text-white placeholder-gray-600 focus:outline-none focus:border-copper transition-colors"
                      placeholder="Your Company"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-semibold text-white mb-2">
                      Service Interest *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full bg-charcoal-dark border border-charcoal-light rounded-xl h-12 px-4 text-base text-white focus:outline-none focus:border-copper transition-colors cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="technology">Technology & Digital Services</option>
                      <option value="healthcare">Healthcare Operations</option>
                      <option value="logistics">Logistics & Transportation</option>
                      <option value="fuel-retail">Gasoline Retail & C-Stores</option>
                      <option value="capital-advisory">Business & Capital Advisory</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-white mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full bg-charcoal-dark border border-charcoal-light rounded-xl p-4 text-base text-white placeholder-gray-600 focus:outline-none focus:border-copper transition-colors resize-none"
                      placeholder="Tell us about your project or inquiry..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-copper hover:bg-copper-light text-white py-4 rounded-full text-base font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                      <p className="text-green-400 text-sm">Thank you! Your message has been sent successfully.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                      <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;